import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useContent } from '../contexts/ContentContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  'Residential Pool Cleaning',
  'Pool Repair & Maintenance',
  'Pool Renovations',
  'Commercial Services',
  'One-Time Cleaning',
  'Other / General Inquiry'
];

export const Contact = () => {
  const { contact, global } = useContent();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: global.phone,
      link: `tel:${global.phone}`
    },
    {
      icon: Mail,
      title: 'Email',
      details: global.email,
      link: `mailto:${global.email}`
    },
    {
      icon: MapPin,
      title: 'Service Area',
      details: global.serviceArea,
      link: null
    },
    {
      icon: Clock,
      title: 'Hours',
      details: global.hours,
      link: null
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.phone || !formData.service) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Thank you! We\'ll be in touch soon.');
    } catch (error) {
      toast.error('Something went wrong. Please try calling us instead.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Helmet>
        <title>Contact Us | Get a Free Pool Service Quote | A-Quality Pool Service</title>
        <meta name="description" content="Contact A-Quality Pool Service for a free pool cleaning, repair, or renovation quote. Serving Tampa Bay and Central Florida. Call 813-453-5988 or fill out our online form." />
        <link rel="canonical" href="https://a-qualitypools.net/contact" />
      </Helmet>
      <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-20 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img loading="lazy" decoding="async"
            src="/images/Wolf_b-1.jpg"
            alt=""
            width={1000}
            height={667}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 to-slate-900" />
        
        <div className="relative max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-sky-400 font-medium mb-4 block">{contact.heroTagline}</span>
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-4 sm:mb-6">
              {contact.heroHeadline}
            </h1>
            <p className="text-base sm:text-xl text-slate-300 max-w-3xl mx-auto">
              {contact.heroSubtext}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Bar */}
      <section className="py-6 sm:py-8 bg-sky-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                {info.link ? (
                  <a
                    href={info.link}
                    data-testid={`contact-info-${info.title.toLowerCase()}`}
                    className="flex flex-col items-center text-white hover:text-white/80 transition-colors"
                  >
                    <info.icon className="h-5 w-5 sm:h-6 sm:w-6 mb-1 sm:mb-2" />
                    <span className="font-medium text-sm sm:text-base">{info.title}</span>
                    <span className="text-xs sm:text-sm text-white/80">{info.details}</span>
                  </a>
                ) : (
                  <div className="flex flex-col items-center text-white">
                    <info.icon className="h-5 w-5 sm:h-6 sm:w-6 mb-1 sm:mb-2" />
                    <span className="font-medium text-sm sm:text-base">{info.title}</span>
                    <span className="text-xs sm:text-sm text-white/80">{info.details}</span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-3">
            <span className="text-white/70 text-xs sm:text-sm">License # CPC1460475</span>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <Card className="shadow-xl border-0">
                <CardContent className="p-8">
                  {isSubmitted ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-12"
                      data-testid="contact-success"
                    >
                      <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                        <CheckCircle className="h-8 w-8 text-green-500" />
                      </div>
                      <h3 className="text-2xl font-bold text-slate-900 mb-2">Thank You!</h3>
                      <p className="text-slate-600 mb-6">
                        We've received your message and will get back to you within 24 hours.
                      </p>
                      <Button
                        onClick={() => {
                          setIsSubmitted(false);
                          setFormData({ name: '', email: '', phone: '', service: '', message: '' });
                        }}
                        variant="outline"
                        className="rounded-full"
                      >
                        Send Another Message
                      </Button>
                    </motion.div>
                  ) : (
                    <>
                      <h2 className="text-2xl font-bold text-slate-900 mb-2">{contact.formTitle}</h2>
                      <p className="text-slate-600 mb-8">
                        {contact.formSubtext}
                      </p>

                      <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="name">Full Name *</Label>
                            <Input
                              id="name"
                              name="name"
                              data-testid="contact-name"
                              value={formData.name}
                              onChange={handleChange}
                              placeholder="John Smith"
                              className="rounded-xl"
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="phone">Phone Number *</Label>
                            <Input
                              id="phone"
                              name="phone"
                              type="tel"
                              data-testid="contact-phone"
                              value={formData.phone}
                              onChange={handleChange}
                              placeholder="(813) 555-1234"
                              className="rounded-xl"
                              required
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            data-testid="contact-email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="john@example.com"
                            className="rounded-xl"
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="service">Service Needed *</Label>
                          <Select
                            value={formData.service}
                            onValueChange={handleServiceChange}
                          >
                            <SelectTrigger
                              data-testid="contact-service"
                              className="rounded-xl"
                            >
                              <SelectValue placeholder="Select a service" />
                            </SelectTrigger>
                            <SelectContent>
                              {serviceOptions.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="message">Message (Optional)</Label>
                          <Textarea
                            id="message"
                            name="message"
                            data-testid="contact-message"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="Tell us about your pool and any specific needs..."
                            className="rounded-xl min-h-[120px]"
                          />
                        </div>

                        <Button
                          type="submit"
                          data-testid="contact-submit"
                          disabled={isSubmitting}
                          className="w-full bg-sky-500 hover:bg-sky-400 text-white rounded-full py-6 text-lg"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="mr-2 h-5 w-5" />
                              Request Free Quote
                            </>
                          )}
                        </Button>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Contact Info & Map */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Get In Touch</h2>
                <p className="text-slate-600 mb-6">
                  Prefer to talk? Give us a call or stop by. We're always happy to help.
                </p>

                <div className="space-y-4">
                  {contactInfo.map((info) => (
                    <div
                      key={info.title}
                      className="flex items-start p-4 bg-white rounded-2xl shadow-sm"
                    >
                      <div className="p-3 bg-sky-500/10 rounded-xl mr-4">
                        <info.icon className="h-6 w-6 text-sky-500" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">{info.title}</h4>
                        {info.link ? (
                          <a
                            href={info.link}
                            className="text-slate-600 hover:text-sky-500 transition-colors"
                          >
                            {info.details}
                          </a>
                        ) : (
                          <p className="text-slate-600">{info.details}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Service Area */}
              <Card className="overflow-hidden border-0 shadow-xl">
                <div className="h-[300px] bg-slate-200 relative">
                  <iframe
                    title="Service Area Map"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d225487.0972899477!2d-82.60992267834397!3d27.964157!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88c2b782b3b9d1e1%3A0xa75f1389af96b463!2sTampa%2C%20FL!5e0!3m2!1sen!2sus!4v1703123456789!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-2">{contact.mapTitle}</h3>
                  <p className="text-slate-600 text-sm">
                    {contact.mapText}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pt-20 pb-36 bg-sky-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {contact.ctaHeadline}
            </h2>
            <p className="text-xl text-white/90 mb-8">
              {contact.ctaSubtext}
            </p>
            <Button
              asChild
              data-testid="contact-cta-call"
              className="bg-white text-sky-600 hover:bg-slate-100 rounded-full px-12 py-6 text-xl"
            >
              <a href={`tel:${global.phone}`}>
                <Phone className="mr-3 h-6 w-6" />
                {global.phone}
              </a>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
